import type {RequestTiming, ServerExtensions} from "@irati/types/apolloExtensions"

interface ApolloWaterfallParams {
  logging: ServerExtensions["loggingV1"]
}

interface WaterfallRow {
  Request: string
  Status: string
  "Start (ms)": number
  "End (ms)": number
  "Duration (ms)": number
  Waterfall: string
  "Started at (server)": string
  "Ended at (server)": string
}

/** All offsets use the same server clock; client/network time must not be mixed into this scale. */
export function buildApolloWaterfall({logging}: ApolloWaterfallParams): WaterfallRow[] {
  const spans: Array<RequestTiming & {label: string; status: string}> = []
  if (hasTiming(logging)) {
    spans.push({...logging, label: "GraphQL request", status: ""})
  }
  Object.entries(logging.operations).forEach(([name, operation]) => {
    if (hasTiming(operation)) {
      spans.push({...operation, label: `${name} [GraphQL execution]`, status: ""})
    }
  })
  Object.entries(logging.services).forEach(([service, calls]) => {
    calls.forEach((call) => {
      // Older servers and uninstrumented sources still appear in the existing payload logs.
      if (!hasTiming(call)) return
      let label: string
      switch (call._tag) {
        case "ApiData":
          label = `${service}/${call.endpoint} [REST]`
          break
        case "GrpcData":
          label = `${call.service}.${call.method} [gRPC]`
          break
        case "VivupData": {
          const operation = call.query.match(/\b(?:query|mutation)\s+(\w+)/)?.[1]
          label = call.endpoint ? `${service}/${call.endpoint} [Vivup REST]` : `${service}${operation ? `.${operation}` : ""} [Vivup]`
          break
        }
        case "AlgoliaData":
          label = `${service} [Algolia]`
          break
      }
      spans.push({...call, label, status: call.status ?? ""})
    })
  })
  if (!spans.length) return []

  spans.sort((a, b) => a.startedAt - b.startedAt)
  const origin = spans[0].startedAt
  const end = spans.reduce((latest, span) => Math.max(latest, span.endedAt), origin)
  const width = 40
  const scale = end - origin || 1

  return spans.map(({label, status, startedAt, endedAt}) => {
    const offset = Math.min(width - 1, Math.floor(((startedAt - origin) / scale) * width))
    const length = Math.max(1, Math.ceil(((endedAt - origin) / scale) * width) - offset)
    return {
      Request: label,
      Status: status,
      "Start (ms)": round(startedAt - origin),
      "End (ms)": round(endedAt - origin),
      "Duration (ms)": round(endedAt - startedAt),
      Waterfall: ("·".repeat(offset) + "█".repeat(length)).padEnd(width, "·"),
      "Started at (server)": new Date(startedAt).toISOString(),
      "Ended at (server)": new Date(endedAt).toISOString()
    }
  })
}

function hasTiming(timing: Partial<RequestTiming>): timing is RequestTiming {
  return (
    typeof timing.startedAt === "number" &&
    typeof timing.endedAt === "number" &&
    // Date range check (±8.64e15 ms): out-of-range values become NaN and would break toISOString().
    Number.isFinite(new Date(timing.startedAt).getTime()) &&
    Number.isFinite(new Date(timing.endedAt).getTime()) &&
    timing.endedAt >= timing.startedAt
  )
}

function round(ms: number) {
  return Math.round(ms * 10) / 10
}
